import { useParams } from 'react-router-dom';
import { trpc } from 'src/trpc';
import { Loader } from 'components/Loader';
import { MainLayout } from 'src/pages/MainLayout';
import { StatusDisplay } from 'components/StatusDisplay';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LucidePencil, MapPin } from 'lucide-react';
import { AssignContainerForm } from './AssignContainersForm';
import { formatTime } from 'src/util';
import { UpdateOrderStatus } from 'components/orders/Details/UpdateOrderStatus';
import { ContainerDetails } from 'components/orders/Details/ContainerDetails';
import { OrderAction, OrderStatusEnum } from '@libs/schemas/src/order';
import { PricingTypeEnum } from '@libs/schemas/src/pricing-rule';
import { WasteProcessingForm } from 'components/orders/Details/WasteProcessingForm';
import { LuTriangleAlert, LuCircleCheck, LuCircleX } from 'react-icons/lu';
import { formatPhone } from 'src/utils/format-phone';
import { PromotedNotes } from 'components/orders/Details/PromotedNotes';
import { JobSummary } from 'components/orders/Details/JobSummary';
import { ChargeSummary } from 'src/components/ChargeSummary';
import { Menu } from 'components/Menu';
import { useState, useMemo, useRef, useEffect } from 'react';
import { ConfirmDialogButton } from 'components/ConfirmDialog';
import { Customer } from 'src/trpc';
import { RestrictedMaterial as Material } from '@libs/schemas/src/material';

import { Discount, itemizedOrderWithAdjustments, safeMoneyFromCents } from '@libs/services/money';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { OrderDeliveryEditSheet } from 'components/orders/Details/OrderDeliveryEditSheet/OrderDeliveryEditSheet';
import { OrderCustomerCorrectionSheet } from 'components/orders/Details/OrderCustomerCorrectionSheet/OrderCustomerCorrectionSheet';
import { DeliveryLocationType, deliveryLocationTypeMap } from '@libs/schemas';
import { FailedPaymentNotificationDialog } from 'components/FailedPaymentNotification';
import { PickupActualBadge } from 'src/components/PickupActualBadge';
import { ActivityDrawer } from 'components/orders/Details/ActivityDrawer';
import { RelatedOrders } from 'components/orders/Details/RelatedOrders';
import { OrderDeliveryLocationMap } from 'components/orders/Details/OrderDeliveryLocationMap';
import { OrderPricingSection } from 'components/orders/Details/OrderPricingSection';
import { OrderAddOnsDialog } from 'components/orders/Details/OrderAddOnsDialog';
import {
  extractChargeSummaryPropsFromFinalizedOrder,
  formatCalendarDate,
  formatDateTime,
} from '@libs/frontend-services';
import { PlacementPhotoUpload } from 'components/placement-photos/PlacementPhotoUpload';
import { usePlacementPhotos } from 'src/hooks/usePlacementPhotos';
import { useOrderCurrentPricing } from 'components/orders/Details/useOrderCurrentPricing';

const sanitizeLineItems = (
  lineItems?: { label: string; amount: number; postTax: boolean }[],
): { label: string; amount: number; postTax: boolean }[] => {
  if (!lineItems) return [];

  return lineItems.filter((item) => {
    // Label must be non-empty string
    if (typeof item.label !== 'string' || item.label.trim().length === 0) return false;
    // Amount must be positive
    if (typeof item.amount !== 'number' || item.amount <= 0) return false;
    // postTax must be boolean
    if (typeof item.postTax !== 'boolean') return false;
    return true;
  });
};

const OrderTags = ({
  materials,
  customer,
  immediateDropoff,
  deliveryLocationType,
}: {
  materials: Material[];
  customer: Customer;
  immediateDropoff: boolean;
  deliveryLocationType?: DeliveryLocationType;
}) => {
  const hasHazardousMaterials = materials.find((m) => m.isRestricted);
  const badges = [
    ...(customer.company
      ? [
          <Badge key="business" variant="informational">
            Business
          </Badge>,
        ]
      : []),
    ...(hasHazardousMaterials
      ? [
          <Badge key="hazmat" variant="destructive">
            Hazmat
          </Badge>,
        ]
      : []),
    ...(immediateDropoff
      ? [
          <Badge key="Urgent" variant="destructive">
            Urgent
          </Badge>,
        ]
      : []),
    ...(deliveryLocationType
      ? [
          <Badge key="property" variant="informational">
            {deliveryLocationTypeMap[deliveryLocationType]}
          </Badge>,
        ]
      : []),
  ];
  if (!badges.length) {
    return null;
  }

  return badges;
};

export const OrderDetailsPage = () => {
  const params = useParams();
  const utils = trpc.useUtils();
  const [totalBalanceInCents, setTotalBalanceInCentsState] = useState<number>(0);

  // The setter from useState is stable, so no useCallback is needed here.
  const setTotalBalanceInCents = (value: number | ((prev: number) => number)) => {
    setTotalBalanceInCentsState((prevValue) => {
      const newValue = typeof value === 'function' ? value(prevValue) : value;
      // Only update if value actually changed
      if (prevValue === newValue) {
        return prevValue;
      }
      return newValue;
    });
  };
  const { data, isLoading, isFetching } = trpc.admin.order.adminGetOrderRelatedInfoById.useQuery(
    params.id!,
  );
  const { data: allMaterials, isLoading: isMaterialLoading } =
    trpc.admin.material.getMaterials.useQuery();
  const { isLoading: isCleanupsLoading } = trpc.admin.cleanup.getCleanups.useQuery({});
  const { data: expandedContainers, isLoading: expandedContainersLoading } =
    trpc.admin.order.adminExpandOrderContainers.useQuery(params.id!);
  const { mutate, isPending: failedPaymentLoading } =
    trpc.admin.order.adminProcessFailedPayment.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(params.id!);
      },
    });
  const [editModal, setEditModal] = useState<null | 'dropoff'>(null);
  const [deliverySheetTab, setDeliverySheetTab] = useState<'address-schedule' | 'delivery-details'>(
    'address-schedule',
  );
  const [isCustomerCorrectionOpen, setIsCustomerCorrectionOpen] = useState(false);
  // The drawer is the sole way to change an order's customer, so the pencil that opens it is
  // the control the keyboard has to come back to when it closes.
  const customerCorrectionTriggerRef = useRef<HTMLButtonElement>(null);
  const [isAddOnsEditorOpen, setIsAddOnsEditorOpen] = useState(false);
  // The add-ons sheet is mounted only while it is open, the way the Actions menu mounts it, so
  // its two catalogue queries stay off the page until someone asks for the editor. That also
  // means it is gone before Radix can hand focus back, so we remember the control that opened
  // it and restore focus once the sheet has actually left the tree - focusing any sooner just
  // loses out to the closing sheet's own focus management.
  const addOnsEditorTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isAddOnsEditorOpen && addOnsEditorTriggerRef.current) {
      addOnsEditorTriggerRef.current.focus();
      addOnsEditorTriggerRef.current = null;
    }
  }, [isAddOnsEditorOpen]);

  // Placement photos hook
  const placementPhotos = usePlacementPhotos({
    orderId: params.id!,
  });

  // The header, Actions menu and pricing breakdown all use this billing-aligned calculation.
  // It is called before the loader return so hook ordering stays stable while the page loads.
  const currentPricing = useOrderCurrentPricing(data?.order, data?.cart);

  // ALL hooks MUST be declared before any conditional returns
  // Memoize discounts array - safe to access data here with optional chaining
  const memoizedDiscounts = useMemo(() => {
    return data?.order?.discounts
      ? data.order.discounts.map((d) =>
          Discount({
            amount: d.percentage > 0 ? d.percentage : d.amountInCents,
            type: d.percentage > 0 ? 'percent' : 'dollars',
          }),
        )
      : [];
  }, [data?.order?.discounts]);

  // Extract ChargeSummary props for PaymentFailed status
  const paymentFailedChargeSummaryProps = useMemo(() => {
    if (
      !data?.order ||
      !data?.cart ||
      !data?.containers ||
      data.order.status !== OrderStatusEnum.PaymentFailed
    ) {
      return null;
    }
    return extractChargeSummaryPropsFromFinalizedOrder(
      data.order,
      data.cart,
      data.containers,
      data.containerBreakdown,
    );
  }, [data]);

  // Early return after ALL hooks have been declared
  if (
    isLoading ||
    !data ||
    expandedContainersLoading ||
    !expandedContainers ||
    isMaterialLoading ||
    isCleanupsLoading
  ) {
    return (
      <MainLayout>
        <Loader title="Loading Order Details" />
      </MainLayout>
    );
  }

  // Now data is guaranteed to exist
  const { order, user, address, cart, containers, customer, materials, containerBreakdown } = data;
  const restrictions = address?.restrictions || {};
  const dropOffContainers = expandedContainers.filter((c) => c.action === OrderAction.DropOff);
  const pickupContainers = expandedContainers.filter((c) => c.action === OrderAction.Pickup);

  const OrderCancelled = () => {
    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg border-disabled bg-secondary border p-4 items-start text-muted w-full">
          <LuCircleX className="w-5 h-5 flex-shrink-0 text-muted" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="text-sm">Order Cancelled</h3>
            <p className="text-sm">
              The order was cancelled on{' '}
              <b>{formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderPickupRequested = () => {
    const formattedDate = formatCalendarDate(order.finalpickupWindow?.day!, 'MMMM d, yyyy');

    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg bg-success-muted border-disabled border p-4 items-start w-full">
          <LuCircleCheck className="w-5 h-5 flex-shrink-0 text-success" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-success text-sm">Pickup Requested</h3>
            <p className="text-success text-sm">
              The order was requested to be picked up on{' '}
              <b>
                {formattedDate} between{' '}
                {formatTime(order.finalpickupWindow?.start!, order.finalpickupWindow?.end!)}
              </b>
              .
            </p>
            {order.quotedPricingDetails?.pricingModel === PricingTypeEnum.FIXED && (
              <p className="text-success text-sm mt-2">
                <b>Single Material Order:</b> This order uses single-material pricing
                {cart.materials && cart.materials.length > 0 && allMaterials && (
                  <> for {allMaterials.find((m) => m._id === cart.materials?.[0]?.id)?.name}</>
                )}
                .
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const OrderCompleted = () => {
    // Safely get final payment (handles undefined/empty array)
    const finalPayment =
      order.payments && order.payments.length > 0
        ? order.payments[order.payments.length - 1]
        : null;

    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg bg-success-muted border-disabled border p-4 items-start w-full">
          <LuCircleCheck className="w-5 h-5 flex-shrink-0 text-success" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-success text-sm">Order Completed</h3>
            <p className="text-success text-sm">
              {finalPayment ? (
                <>
                  Final payment of <b>{safeMoneyFromCents(finalPayment.amountPaid).toFormat()}</b>{' '}
                  was made on{' '}
                </>
              ) : (
                <>No payment was made for this order. Completed on </>
              )}
              <b>{formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderDropOffConfirmed = () => {
    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg bg-success-muted border-disabled border p-4 items-start w-full">
          <LuCircleCheck className="w-5 h-5 flex-shrink-0 text-success" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-success text-sm">Dumpster(s) Delivered</h3>
            <p className="text-success text-sm">
              The dumpster(s) were delivered on{' '}
              <b>{formatDateTime(order.deliveryConfirmedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderPaymentFailed = () => {
    // Check if order has no payment method
    const hasNoPaymentMethod =
      !order.payments ||
      order.payments.length === 0 ||
      !order.payments.find((p) => 'paymentMethodId' in p && p.paymentMethodId);

    return (
      <div className="card border-0 w-full px-0 py-4">
        <div className="flex flex-row gap-3 rounded-lg bg-error-muted border-error border p-4 items-start w-full">
          <LuTriangleAlert className="w-5 h-5 flex-shrink-0 text-error" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-error text-sm">Payment Failed</h3>
            <p className="text-error text-sm">
              {hasNoPaymentMethod ? (
                <>
                  This order has no payment method associated. A payment method must be added before
                  charging the remaining balance of{' '}
                  <span className="font-semibold">
                    {safeMoneyFromCents(totalBalanceInCents).toFormat()}
                  </span>
                  .
                </>
              ) : (
                <>
                  A payment in the amount of{' '}
                  <span className="font-semibold">
                    {safeMoneyFromCents(totalBalanceInCents).toFormat()}
                  </span>{' '}
                  failed on <b>{formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderRefunded = () => {
    const refund = order.refunds ? order.refunds[order.refunds.length - 1] : null;

    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg bg-success-muted border-disabled border p-4 items-start w-full">
          <LuCircleCheck className="w-5 h-5 flex-shrink-0 text-success" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-success text-sm">Order Completed</h3>
            <p className="text-success text-sm">
              <b>{safeMoneyFromCents(refund?.amountRefunded!).toFormat()}</b> was refunded to the
              customer after adjustment on{' '}
              <b>{formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderRefundFailed = () => {
    // Calculate the intended refund amount using the same logic as processOrderPaymentOrRefund
    // This matches the exact calculation that would have been used for the failed refund
    const calculateIntendedRefundAmount = () => {
      if (!order.finalizedLineItems || !order.payments) return 0;

      const finalizedItems = order.finalizedLineItems;

      // Use the same itemizedOrderWithAdjustments logic as ChargeSummary and processOrderPaymentOrRefund
      const itemizedOrder = itemizedOrderWithAdjustments({
        order: { ...order, cart },
        adjustedWeightTotal: safeMoneyFromCents(finalizedItems.weight.adjustCostInCents),
        adjustedContainerTotal: safeMoneyFromCents(finalizedItems.containersAdjustedCostInCents),
        adjustedMaterialTotal: safeMoneyFromCents(
          finalizedItems.additionalMaterialChargesAdjustedCostInCents,
        ),
      });

      // Calculate balance using the same formula as processOrderPaymentOrRefund
      // balance = totalBeforeTax + tax + postTaxAdditionalLineItems + existingRefunds - totalPaid
      const taxAmount = cart?.lineItems?.taxInCents || 0;
      const existingRefunds = order.refunds?.reduce((acc, p) => acc + p.amountRefunded, 0) || 0;
      const totalPaid = order.payments.reduce(
        (acc, p) => acc + p.amountPaid - (p.amountRefunded || 0),
        0,
      );

      const balance = itemizedOrder.totalBeforeTax
        .add(safeMoneyFromCents(taxAmount))
        .add(itemizedOrder.postTaxAdditionalLineItemsTotal)
        .add(safeMoneyFromCents(existingRefunds))
        .subtract(safeMoneyFromCents(totalPaid));

      // If balance is negative, customer is owed money (refund amount)
      return balance.isNegative() ? Math.abs(balance.getAmount()) : 0;
    };

    const intendedRefundAmount = calculateIntendedRefundAmount();

    // Check if order has no payment method
    const hasNoPaymentMethod =
      !order.payments ||
      order.payments.length === 0 ||
      !order.payments.find((p) => 'paymentMethodId' in p && p.paymentMethodId);

    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg bg-error-muted border-error border p-4 items-start w-full">
          <LuTriangleAlert className="w-5 h-5 flex-shrink-0 text-error" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className=" text-error text-sm">Refund Failed</h3>
            <p className="text-error text-sm">
              {hasNoPaymentMethod ? (
                <>
                  This order has no payment method associated. The customer cannot be refunded{' '}
                  <span className="font-semibold">
                    {safeMoneyFromCents(intendedRefundAmount).toFormat()}
                  </span>{' '}
                  until a payment method is added.
                </>
              ) : (
                <>
                  The refund of{' '}
                  <span className="font-semibold">
                    {safeMoneyFromCents(intendedRefundAmount).toFormat()}
                  </span>{' '}
                  failed on <b>{formatDateTime(order.updatedAt, "MMMM d, yyyy 'at' h:mm a")}</b>.
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const OrderRestricted = () => {
    return (
      <div className="card border-0 p-4">
        <div className="flex flex-row gap-3 rounded-lg border-warning bg-warning-muted border p-4 items-start text-warning w-full">
          <LuTriangleAlert className="w-5 h-5 flex-shrink-0 text-warning" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="text-sm">Hazardous Material Detected</h3>
            <p className="text-sm">
              This order contains restricted materials and requires manual review. Please contact
              the customer to confirm details and proceed with scheduling.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const RenderActionSection = () => {
    if (isLoading || isFetching) {
      return (
        <div className="card flex items-center justify-center p-8">
          <Loader title="Loading order status..." h={6} w={6} align="horizontal" />
        </div>
      );
    }

    if (
      [OrderStatusEnum.Created, OrderStatusEnum.PendingDropOff].includes(
        order.status as OrderStatusEnum,
      )
    ) {
      return <AssignContainerForm order={order} action={OrderAction.DropOff} />;
    }

    if (
      [
        OrderStatusEnum.DropOffScheduled,
        OrderStatusEnum.InTransitDropOff,
        OrderStatusEnum.PickupScheduled,
        OrderStatusEnum.InTransitPickup,
        OrderStatusEnum.PickupConfirmed,
      ].includes(order.status as OrderStatusEnum)
    ) {
      return <UpdateOrderStatus order={order} />;
    }

    if (
      [OrderStatusEnum.PickupRequested, OrderStatusEnum.PendingPickup].includes(
        order.status as OrderStatusEnum,
      )
    ) {
      return (
        <>
          <OrderPickupRequested />
          <AssignContainerForm order={order} action={OrderAction.Pickup} />
        </>
      );
    }

    if (order.status === OrderStatusEnum.Cancelled) {
      return <OrderCancelled />;
    }

    if (order.status === OrderStatusEnum.WasteProcessing) {
      return <WasteProcessingForm order={order} containers={containers} cart={cart} />;
    }

    if (order.status === OrderStatusEnum.Completed) {
      return <OrderCompleted />;
    }

    if (order.status === OrderStatusEnum.Refunded) {
      return <OrderRefunded />;
    }

    if (order.status === OrderStatusEnum.RefundFailed) {
      return <OrderRefundFailed />;
    }

    if (order.status === OrderStatusEnum.DropOffConfirmed) {
      return <OrderDropOffConfirmed />;
    }

    if (order.status === OrderStatusEnum.PaymentFailed) {
      return (
        <>
          <div className="card flex flex-col border-0 pt-0 px-4 pb-4">
            <OrderPaymentFailed />
            <ChargeSummary
              {...(paymentFailedChargeSummaryProps || { order, cart })}
              setTotalBalanceInCents={setTotalBalanceInCents}
            >
              {() => (
                <div className="flex flex-col bg-white w-full">
                  <div className="h-[1px] bg-disabled my-6"></div>
                  <div className="flex flex-row gap-2 items-center w-full">
                    <FailedPaymentNotificationDialog orderId={order._id!} />
                    {!failedPaymentLoading && (
                      <ConfirmDialogButton
                        isLoading={failedPaymentLoading}
                        openButtonText={'Charge Remaining Balance'}
                        headerText={'Charge Remaining Balance'}
                        bodyText={`Order #${order.friendlyId} has a remaining balance of ${safeMoneyFromCents(totalBalanceInCents).toFormat()}.             
                            \nAre you sure you want to charge the customer?`}
                        onConfirm={() => {
                          mutate(order._id!);
                        }}
                      />
                    )}
                    {failedPaymentLoading && (
                      <div className="w-full flex justify-center">
                        <Loader title="Processing Payment" h={6} w={6} />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ChargeSummary>
          </div>
        </>
      );
    }

    if (order.status === OrderStatusEnum.Restricted) {
      return <OrderRestricted />;
    }

    return null;
  };

  return (
    <MainLayout>
      <OrderDeliveryEditSheet
        isOpen={editModal === 'dropoff'}
        onClose={() => setEditModal(null)}
        initialTab={deliverySheetTab}
      />

      <OrderCustomerCorrectionSheet
        isOpen={isCustomerCorrectionOpen}
        onClose={() => setIsCustomerCorrectionOpen(false)}
        restoreFocusRef={customerCorrectionTriggerRef}
      />
      {/* Everything this page renders inline sits under one class, and that class is what
          scopes the mobile type scale in index.css. The sheets above are portalled out of this
          subtree, so they keep their own sizes, as does the surrounding admin shell.

          The wrapper also carries the gap MainLayout was putting between the header row and the
          body, now that those two are one child instead of two. */}
      <div className="order-detail-mobile-type flex flex-col gap-6 w-full min-w-0">
        {/* At the mobile scale the Actions menu no longer fits beside a 30px order number, so
            the header row wraps instead of squeezing either one. */}
        <div className="flex flex-row flex-wrap justify-between items-center gap-2">
          <div className="flex flex-col gap-2 min-w-0">
            <h1 className="font-semibold text-xl">Order #{order?.friendlyId}</h1>
            {/* Status, pickup and tag badges take as many lines as they need rather than
                pushing the page sideways. */}
            <div className="flex flex-row flex-wrap gap-2 items-center">
              <p className="text-sm text-muted">
                {formatDateTime(order.createdAt, "MMMM dd, yyyy 'at' hh:mm a")}
              </p>
              <StatusDisplay className="text-xs" status={order?.status!} />
              {order.pickupWindow?.isActualPickupDate && <PickupActualBadge className="text-xs" />}
              <OrderTags
                immediateDropoff={order.dropOffWindow?.immediateDropOffRequested || false}
                materials={materials as Material[]}
                customer={customer as Customer}
                deliveryLocationType={order.deliveryLocationType}
              />
              {(currentPricing.outstandingBalanceInCents || 0) > 0 && (
                <Badge variant="informational">Outstanding Balance</Badge>
              )}
            </div>
          </div>
          {order.status !== OrderStatusEnum.Restricted && (
            <Menu
              discounts={memoizedDiscounts}
              lineItems={sanitizeLineItems(order.additionalLineItems)}
              addOns={order.addOns || []}
              showTotalTable={order.status !== OrderStatusEnum.WasteProcessing}
              orderTotal={currentPricing.totalInCents || 0}
              pricingOrder={order}
              pricingCart={cart}
              orderId={order._id!}
              orderStatus={order.status}
              userId={order.userId}
            />
          )}
        </div>
        {/* The admin shell changes the available content width without changing the viewport.
            The grid stacks until its own width can hold two 400px columns. */}
        <div className="order-details-columns mt-4 w-full min-w-0">
          <div className="flex flex-col gap-4 w-full min-w-0">
            <div className="flex flex-col gap-4 w-full min-w-0">
              <h3 className="font-semibold text-lg">Order Details</h3>
              {/* Who the order is for comes first. The job summary used to lead, which meant
                  admins scrolled past the operational detail to find the customer - the one
                  thing they check on every order. Moved as real JSX rather than CSS order, so
                  the reading order a screen reader and the tab sequence follow matches what is
                  on screen, and the customer pencil precedes the add-ons pencil. */}
              {/* The card follows its flex column instead of imposing a 400px floor. At 390px
                  that floor held the card at 400px wide and pushed the pencil past the right
                  edge, where the shell suppresses the overflow and the control cannot be
                  reached. */}
              <div className="card relative min-w-0 w-full flex flex-col gap-4">
                {/* 48px kept clear for the 44px pencil so a long customer name never renders
                    underneath it. */}
                <div className="flex flex-col gap-1 w-full pr-12">
                  {/* The one control for changing who an order belongs to. It used to open a
                      profile edit dialog while a second text button opened this drawer, which
                      read as two ways to do the same thing. */}
                  <Button
                    ref={customerCorrectionTriggerRef}
                    size="icon"
                    className="absolute top-0 right-0 h-11 w-11 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    variant="ghost"
                    aria-label="Change order customer"
                    title="Change order customer"
                    onClick={() => setIsCustomerCorrectionOpen(true)}
                  >
                    <LucidePencil className="w-4 h-4" />
                  </Button>
                  <label className="label text-xs">Customer Details</label>
                  <p className="text-text-primary text-sm font-medium underline break-words">
                    {user.name?.first} {user.name?.last}
                  </p>
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <label className="label text-xs">Contact Information</label>
                  {/* Long addresses wrap rather than widening the card back into overflow. */}
                  <p className="text-text-primary text-sm font-medium break-words">{user.email}</p>
                  <p className="text-text-primary text-sm font-medium">
                    {formatPhone(user.phone.replace('+1', ''))}
                  </p>
                </div>
              </div>

              {/* Price-free operational summary, directly under Customer Details for
                  dispatch/driver verification. Additive - the detailed cards below are
                  unchanged.

                  The pencil opens the same OrderAddOnsDialog the Actions menu opens, under the
                  same eligibility: a restricted order has no priced add-on list to manage yet,
                  so it gets no edit affordance here either. */}
              <JobSummary
                order={order}
                cart={cart}
                allMaterials={allMaterials}
                onEdit={
                  order.status === OrderStatusEnum.Restricted
                    ? undefined
                    : () => {
                        addOnsEditorTriggerRef.current =
                          document.activeElement as HTMLElement | null;
                        setIsAddOnsEditorOpen(true);
                      }
                }
              />
              {isAddOnsEditorOpen && (
                <OrderAddOnsDialog
                  open={isAddOnsEditorOpen}
                  onClose={() => setIsAddOnsEditorOpen(false)}
                  orderId={order._id!}
                  addOns={order.addOns || []}
                />
              )}
              {address ? (
                <div className="card min-w-0 w-full flex flex-col gap-4 justify-start relative">
                  <Button
                    size="icon"
                    className="absolute top-0 right-0 h-11 w-11 hover:text-primary"
                    variant="ghost"
                    aria-label="Edit drop-off address"
                    title="Edit drop-off address"
                    onClick={() => {
                      setDeliverySheetTab('address-schedule');
                      setEditModal('dropoff');
                    }}
                  >
                    <LucidePencil className="w-4 h-4" />
                  </Button>
                  {/* Same 48px pencil clearance the customer card keeps, so a wrapped address
                      never runs underneath the control. */}
                  <div className="flex flex-col gap-1 w-full pr-12">
                    {/* Edited from the pencil above - the delivery edit sheet is the only
                        entry point for changing the service address. */}
                    <label className="label text-xs">Drop-off Address</label>
                    <p className="text-text-primary text-sm font-medium break-words">
                      {address.line1}
                      {address.line2 ? ` ${address.line2}` : ''}, {address.city}, {address.state}{' '}
                      {address.postalCode}
                    </p>
                  </div>
                  {!!Object.keys(restrictions).length && (
                    <div className="flex flex-col gap-1 w-full">
                      <label className="label text-xs">Delivery Details</label>
                      {Object.keys(restrictions).map((restriction) => (
                        <p
                          className="text-text-primary text-sm font-medium break-words"
                          key={restriction}
                        >
                          {restriction}
                          {restriction in restrictions && ':'} {restrictions[restriction]}
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="h-[1px] bg-slate-200 w-full"></div>
                  {/* "Approximate Pick-up Date / Time" over a full date is already two lines at
                      21px; side by side the pair would break onto four each. */}
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-4 w-full">
                    {order?.dropOffWindow && (
                      <div className="flex flex-col gap-1 w-full">
                        <label className="label text-xs">Drop-off Date / Time</label>
                        {order.dropOffWindow.immediateDropOffRequested ? (
                          <p className="text-text-primary text-sm font-medium">
                            Immediately needed
                          </p>
                        ) : (
                          <p className="text-text-primary text-sm font-medium">
                            {formatCalendarDate(order.dropOffWindow.day, 'EEE MMM do yyyy')}
                          </p>
                        )}
                        <p className="text-text-primary text-sm font-medium">
                          {formatTime(order.dropOffWindow.start, order.dropOffWindow.end)}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col gap-1 w-full">
                      <label className="label text-xs">
                        {order?.pickupWindow?.isActualPickupDate ? 'Actual' : 'Approximate'} Pick-up
                        Date / Time
                      </label>
                      {order?.pickupWindow ? (
                        <>
                          <p className="text-text-primary text-sm font-medium">
                            {formatCalendarDate(order.pickupWindow.day!, 'EEE MMM do yyyy')}
                          </p>
                          <p className="text-text-primary text-sm font-medium">
                            {formatTime(order.pickupWindow.start!, order.pickupWindow.end!)}
                          </p>
                        </>
                      ) : (
                        <p className="text-text-primary text-sm font-medium">
                          No Pickup Date Selected
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                order.status === OrderStatusEnum.Restricted && (
                  <div className="card flex flex-col gap-4 justify-start">
                    <div className="flex flex-col gap-1 w-full">
                      <label className="label text-xs">Drop-off Address</label>
                      <p className="text-text-primary text-sm font-medium">
                        Address not yet provided - pending customer contact
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 w-full">
                      <label className="label text-xs">Delivery Schedule</label>
                      <p className="text-text-primary text-sm font-medium">
                        Schedule not yet set - pending review
                      </p>
                    </div>
                  </div>
                )
              )}

              {/* Placement Details - Instructions and Photos */}
              <div className="card min-w-0 w-full flex flex-col gap-4 justify-start items-start relative">
                <Button
                  size="icon"
                  className="absolute top-0 right-0 h-11 w-11 hover:text-primary"
                  variant="ghost"
                  aria-label="Edit placement details"
                  title="Edit placement details"
                  onClick={() => {
                    setDeliverySheetTab('delivery-details');
                    setEditModal('dropoff');
                  }}
                >
                  <LucidePencil className="w-4 h-4" />
                </Button>

                <label className="label text-sm pr-12">Placement Details</label>

                {/* Instructions Block */}
                {order.placementInstructions ? (
                  <div className="flex items-start gap-2.5 bg-slate-50 rounded-lg p-3 w-full">
                    <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    {/* min-w-0 so long placement instructions wrap inside the flex row instead
                        of forcing it wider than the card. */}
                    <div className="flex flex-col gap-1 min-w-0">
                      <label className="label text-xs">Instructions</label>
                      <p className="text-text-primary text-sm font-medium whitespace-pre-wrap break-words">
                        {order.placementInstructions}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted">No placement instructions provided</p>
                )}

                {/* Placement Photos */}
                <PlacementPhotoUpload
                  className="w-full"
                  photos={(order.placementPhotos || []).map((p) => ({
                    ...p,
                    uploadedAt: new Date(p.uploadedAt),
                  }))}
                  onUploadFiles={placementPhotos.uploadFiles}
                  onDeletePhoto={placementPhotos.deletePhoto}
                  isUploading={placementPhotos.isUploading}
                  uploadingFiles={placementPhotos.uploadingFiles}
                  uploadingFileStates={placementPhotos.uploadingFileStates}
                />
              </div>

              {/* Delivery Location Map - renders its own card */}
              {order.deliveryPinLocation && (
                <OrderDeliveryLocationMap
                  orderId={order._id!}
                  address={address ?? undefined}
                  driver={dropOffContainers.length > 0 ? dropOffContainers[0].driver : null}
                  containerAction="dropoff"
                  pinLocation={order.deliveryPinLocation}
                />
              )}

              <div className="card flex flex-col gap-4 justify-start w-full p-4 mt-2">
                <div className="flex flex-col justify-between w-full">
                  <div className="flex flex-row justify-between  w-full">
                    <p className="label text-xs">Materials</p>
                  </div>
                  <div className="flex flex-col justify-between w-full">
                    {(cart?.materials || []).length > 0 ? (
                      (cart.materials || []).map((material) => (
                        <p
                          key={material.id}
                          className="text-text-primary text-sm font-medium break-words"
                        >
                          {(allMaterials || []).find((m) => m._id === material.id)?.name}
                        </p>
                      ))
                    ) : (
                      <p className="text-sm font-medium text-muted">
                        {order.status === OrderStatusEnum.Restricted
                          ? 'Restricted materials - pending review'
                          : 'No materials selected'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* === PRICING & PAYMENT BREAKDOWN ===
                  The only section on this page collapsed by default, and the page's single
                  pricing render path. OrderPricingSection's existing output is rendered inside
                  unchanged - same components, order, copy, conditions and calculations - and the
                  disclosure uses the same plain pattern as Container Details below.
                  Safe to unmount while collapsed: the payment-failed balance is set by the
                  separate always-mounted ChargeSummary in the Action column. */}
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="pricing-payment-breakdown" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="font-semibold text-lg">Pricing &amp; Payment Breakdown</span>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-4">
                    <OrderPricingSection
                      order={order}
                      cart={cart}
                      address={address}
                      allMaterials={allMaterials}
                      setTotalBalanceInCents={setTotalBalanceInCents}
                      containers={containers}
                      containerBreakdown={containerBreakdown}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* === CONTAINER DETAILS ACCORDION === */}
              {(dropOffContainers.length > 0 || pickupContainers.length > 0) && (
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="container-details" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <span className="font-semibold text-lg">Container Details</span>
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-4">
                      {dropOffContainers.length > 0 && (
                        <div className="flex flex-col gap-4 w-full">
                          <h4 className="font-semibold text-base">Drop-off Details</h4>
                          {dropOffContainers.map((container, ind) => (
                            <ContainerDetails orderContainer={container} key={ind} />
                          ))}
                        </div>
                      )}
                      {pickupContainers.length > 0 && (
                        <div className="flex flex-col gap-4 w-full">
                          <h4 className="font-semibold text-base">Pickup Details</h4>
                          {pickupContainers.map((container, ind) => (
                            <ContainerDetails orderContainer={container} key={ind} />
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </div>
          </div>
          {/* The grid track owns the 400px floor and stacks this column when it cannot fit. */}
          <div className="flex flex-col gap-8 w-full min-w-0 max-w-[650px]">
            <div className="flex flex-col gap-4 w-full min-w-0">
              <h3 className="font-semibold text-lg">Action</h3>
              <RenderActionSection />
            </div>
            <RelatedOrders orderId={order._id!} />
            <div className="flex flex-col gap-6 w-full min-w-0">
              <div className="flex flex-row flex-wrap justify-between items-center gap-2">
                <h3 className="font-semibold text-lg">Notes</h3>
                <ActivityDrawer order={order} />
              </div>
              <PromotedNotes entityId={order._id!} origin="order" />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
